import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSpring } from 'framer-motion';
import { motion } from '../lib/motionShim';
import { usePageProgress } from '../lib/useViewportProgress';
import { StonLogoFull } from './StonLogo';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const pageProgress = usePageProgress();
  const readProgress = useSpring(pageProgress, { stiffness: 120, damping: 30 });

  // Plain scroll listener — glass background past 50px, hide on scroll-down
  React.useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      setIsHidden(y > 150 && y > lastY);
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navigate = useNavigate();

  // Section links work from any page: on the home page they scroll straight
  // there; elsewhere they route home first, then scroll once it has mounted.
  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      setIsMobileMenuOpen(false);
      const scroll = () =>
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      if (location.pathname === '/') {
        scroll();
      } else {
        navigate('/');
        setTimeout(scroll, 350);
      }
    }
  };

  const navLinks = [
    { label: 'Home', href: '/', isRoute: true },
    { label: 'Product', href: '/product', isRoute: true },
    { label: 'Story', href: '#story', isRoute: false },
    { label: 'Results', href: '#proof', isRoute: false },
  ];

  return (
    <>
      <motion.nav
        variants={{
          visible: { y: 0 },
          hidden: { y: "-100%" }
        }}
        animate={isHidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isScrolled ? 'nav-scrolled' : ''
          }`}
        data-testid="navigation"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="nav-logo">
              <StonLogoFull logoSize={32} />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`nav-link text-[15px] font-medium transition-colors ${isActive(link.href)
                        ? 'text-ston-text active'
                        : 'text-ston-text-secondary hover:text-ston-text'
                      }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="nav-link text-[15px] font-medium text-ston-text-secondary hover:text-ston-text transition-colors"
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a
                href="/#waitlist"
                onClick={(e) => handleNavClick(e, '#waitlist')}
                className="inline-flex items-center px-6 py-2.5 bg-white text-ston-bg rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                data-testid="nav-cta-button"
              >
                Get the App
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-ston-text p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Reading progress hairline */}
        <motion.div
          aria-hidden="true"
          className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
          style={{
            scaleX: readProgress,
            background: 'linear-gradient(90deg, var(--ston-purple), var(--ston-purple-light), transparent)',
          }}
        />
      </motion.nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <button
          className="absolute top-6 right-6 text-ston-text p-2"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X size={24} />
        </button>

        {navLinks.map((link) => (
          link.isRoute ? (
            <Link
              key={link.label}
              to={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-2xl font-semibold text-ston-text hover:text-ston-purple transition-colors"
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ) : (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-2xl font-semibold text-ston-text hover:text-ston-purple transition-colors"
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          )
        ))}

        <a
          href="/#waitlist"
          onClick={(e) => handleNavClick(e, '#waitlist')}
          className="mt-8 px-8 py-3 bg-white text-ston-bg rounded-full text-lg font-semibold"
          data-testid="mobile-nav-cta"
        >
          Get the App
        </a>
      </div>
    </>
  );
};

export default Navigation;
